/**
 * Chat Toggle Button (desktop)
 *
 * Pill-style toggle that opens/closes the right-side chat panel.
 * Borrows the mobile MobileTopBar toggle aesthetic (rounded, icon-first,
 * fills with primary when active). Desktop-only — not rendered on mobile.
 */

import { MessageCircle } from 'lucide-react';
import clsx from 'clsx';

interface ChatToggleButtonProps {
  isOpen: boolean;
  onToggle: () => void;
  /** Optional accessible label override */
  label?: string;
  /** When true, pulse the button to direct attention (tutorial preamble). */
  pulse?: boolean;
}

export function ChatToggleButton({ isOpen, onToggle, label, pulse = false }: ChatToggleButtonProps) {
  const ariaLabel = label ?? (isOpen ? 'Close chat' : 'Open chat (Ask Danny)');

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={ariaLabel}
      aria-pressed={isOpen}
      title={ariaLabel + ' (⌘J)'}
      className={clsx(
        'inline-flex items-center gap-1.5 h-8 px-3 rounded-full font-mono text-[11px] uppercase tracking-wider transition-colors whitespace-nowrap shrink-0',
        'border',
        isOpen
          ? 'bg-primary-500 border-primary-500 text-surface-base-dark hover:bg-primary-400 hover:border-primary-400'
          : 'bg-surface-raised-light dark:bg-surface-raised-dark border-border-subtle-light dark:border-border-subtle-dark text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark',
        pulse && !isOpen && 'animate-chat-pulse border-primary-400',
      )}
    >
      <MessageCircle className="w-3.5 h-3.5" />
      <span className="hidden xl:inline">Ask Danny</span>
    </button>
  );
}
